import { useState, useEffect, useCallback } from "react";
import { Menu, X, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import { HomeCareLogoIcon } from "@/components/HomeCareLogoIcon";
import { LanguageSelector } from "@/components/LanguageSelector";

const getBookingHref = () => (window.location.pathname === "/" ? "#booking" : "/#booking");

const NavLinks = ({ links, className, onClickLink }) => (
  <>
    {links.map((link) => (
      <a
        key={link.href}
        href={link.href}
        data-testid={`nav-${link.href.slice(1)}`}
        className={className}
        onClick={onClickLink}
      >
        {link.label}
      </a>
    ))}
  </>
);

const MobileMenu = ({ isOpen, navLinks, onClose, bookLabel }) => {
  if (!isOpen) return null;
  return (
    <div className="lg:hidden mt-4 pb-4 border-t border-[#003A4B]/10 bg-white/95 backdrop-blur-md rounded-b-2xl shadow-lg -mx-6 px-6">
      <div className="flex flex-col gap-4 pt-4">
        <NavLinks
          links={navLinks}
          className="text-[#4A5568] hover:text-[#003A4B] font-medium transition-colors px-2"
          onClickLink={onClose}
        />
        <a href={getBookingHref()} onClick={onClose} data-testid="mobile-book-btn">
          <Button className="bg-[#C89D4B] hover:bg-[#B58938] text-white rounded-full w-full">
            <Calendar className="w-4 h-4 mr-2" />
            {bookLabel}
          </Button>
        </a>
      </div>
    </div>
  );
};

export const Navigation = () => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleClose = useCallback(() => setIsOpen(false), []);

  const navLinks = [
    { href: "#home", label: t.nav.home },
    { href: "#about", label: t.nav.about },
    { href: "#services", label: t.nav.services },
    { href: "#calculator", label: t.nav.quote },
    { href: "#booking", label: t.nav.booking },
    { href: "#contact", label: t.nav.contact },
  ];

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-lg py-2"
          : "bg-transparent py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between">
          <a href="#home" className="flex items-center gap-3 group" data-testid="brand-logo">
            <HomeCareLogoIcon className="h-12 w-auto group-hover:scale-105 transition-transform" />
            <div className="flex flex-col leading-tight">
              <span className="font-['Playfair_Display'] text-lg font-bold text-[#003A4B] tracking-tight">Caceres</span>
              <span className="text-[10px] font-semibold text-[#C89D4B] uppercase tracking-[0.2em]">Essential Care</span>
            </div>
          </a>

          <div className="hidden lg:flex items-center gap-6">
            <NavLinks
              links={navLinks}
              className="text-[#4A5568] hover:text-[#003A4B] font-medium transition-colors relative after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-0.5 after:bg-[#C89D4B] hover:after:w-full after:transition-all text-sm"
            />
            <LanguageSelector />
            <a href={getBookingHref()} data-testid="nav-book-btn">
              <Button className="bg-[#C89D4B] hover:bg-[#B58938] text-white rounded-full px-5 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all text-sm">
                <Calendar className="w-4 h-4 mr-2" />
                {t.hero.bookNow}
              </Button>
            </a>
          </div>

          <div className="lg:hidden flex items-center gap-3">
            <LanguageSelector />
            <button data-testid="mobile-menu-btn" className="p-2 text-[#003A4B]" onClick={() => setIsOpen(!isOpen)}>
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        <MobileMenu isOpen={isOpen} navLinks={navLinks} onClose={handleClose} bookLabel={t.hero.bookNow} />
      </div>
    </nav>
  );
};
